import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FileMinus, Search } from 'lucide-react';
import LoadingSpinner from '../../ui/LoadingSpinner';
import {
  getInventoryItems,
  getWarehouseTransactions,
  createOutputTransaction,
} from '../../../redux/api';
import { su } from '../../inventory/stockUi';
import NumberField, { parseNumber } from '../../inventory/NumberField';

const emptyForm = () => ({
  itemId: '',
  quantity: '',
  reason: '',
  notes: '',
});

const AssetWriteOffView = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState(emptyForm());

  const fetchData = async () => {
    try {
      setLoading(true);
      const [itemsRes, transactionsRes] = await Promise.all([
        getInventoryItems({ stockKind: 'ASSET' }),
        getWarehouseTransactions({ type: 'OUTPUT', stockKind: 'ASSET' }),
      ]);
      setItems(itemsRes.data || []);
      setTransactions(transactionsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const selectedItem = items.find((item) => String(item._id) === formData.itemId);
  const availableQuantity = Number(selectedItem?.availableQuantity ?? selectedItem?.currentQuantity ?? 0);
  const requestedQuantity = parseNumber(formData.quantity, NaN);
  const quantityExceedsStock =
    selectedItem &&
    Number.isFinite(requestedQuantity) &&
    requestedQuantity > 0 &&
    requestedQuantity > availableQuantity;
  const canSubmit =
    selectedItem &&
    !quantityExceedsStock &&
    Number.isFinite(requestedQuantity) &&
    requestedQuantity > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const quantity = parseNumber(formData.quantity);
    if (!quantity || quantity <= 0) {
      toast.error('Unesite količinu veću od 0');
      return;
    }
    if (quantityExceedsStock) {
      toast.error(`Nedovoljna zaliha. Dostupno: ${availableQuantity}`);
      return;
    }

    try {
      await createOutputTransaction({
        itemId: formData.itemId,
        quantity,
        documentNumber: `OTPIS-${Date.now()}`,
        documentDate: new Date().toISOString().split('T')[0],
        reason: formData.reason || 'Otpis sredstva',
        notes: formData.notes,
        stockKind: 'ASSET',
      });
      toast.success('Sredstvo je otpisano');
      setFormData(emptyForm());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Greška pri otpisu');
    }
  };

  const filteredTransactions = transactions.filter((tx) => {
    const search = searchTerm.toLowerCase();
    return (
      tx.itemId?.name?.toLowerCase().includes(search) ||
      tx.itemId?.itemCode?.toLowerCase().includes(search) ||
      tx.reason?.toLowerCase().includes(search) ||
      tx.notes?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className={`${su.page} flex items-center justify-center`}>
        <LoadingSpinner size="lg" color="white" />
      </div>
    );
  }

  return (
    <div className={su.page}>
      <div className={su.wrap}>
        <div className={`${su.card} ${su.cardPad}`}>
          <div className={su.toolbar}>
            <div className="min-w-0 flex-1">
              <h1 className={su.title}>
                <FileMinus className={su.icon} />
                {t('inventory.assetWriteOff.title', 'Otpis sredstava')}
              </h1>
            </div>
            <div className={su.searchWrap}>
              <Search className={su.searchIcon} />
              <input
                type="text"
                placeholder={t('common.search', 'Pretraži...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={su.searchInput}
              />
            </div>
          </div>
        </div>

        <div className={`${su.card} ${su.cardPad}`}>
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className={su.grid}>
              <div>
                <label className={su.label}>Sredstvo *</label>
                <select
                  name="itemId"
                  value={formData.itemId}
                  onChange={handleInputChange}
                  required
                  className={su.input}
                >
                  <option value="">Izaberi sredstvo...</option>
                  {items
                    .filter((item) => Number(item.availableQuantity ?? item.currentQuantity ?? 0) > 0)
                    .map((item) => (
                      <option key={item._id} value={item._id}>
                        {item.itemCode} - {item.name} (dostupno: {item.availableQuantity ?? item.currentQuantity ?? 0})
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className={su.label}>Količina *</label>
                <NumberField
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  required
                  min="0.01"
                  step="0.01"
                  max={availableQuantity || undefined}
                  className={`${su.input}${quantityExceedsStock ? ' border-red-500 focus:ring-red-500 focus:border-red-500' : ''}`}
                />
                {selectedItem && (
                  <span className="text-[11px] text-gray-400 mt-0.5 block">
                    Dostupno: {availableQuantity} {selectedItem.unit || 'kom'}
                  </span>
                )}
                {quantityExceedsStock && (
                  <span className="text-[11px] text-red-400 mt-0.5 block">
                    Nedovoljna zaliha. Dostupno: {availableQuantity}
                  </span>
                )}
              </div>
              <div>
                <label className={su.label}>Razlog</label>
                <input
                  type="text"
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  placeholder="Npr. habanje, kvar, gubitak..."
                  className={su.input}
                />
              </div>
            </div>
            <div>
              <label className={su.label}>Napomena</label>
              <textarea name="notes" value={formData.notes} onChange={handleInputChange} rows={2} className={su.textarea} />
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!canSubmit}
                className={`${su.btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Otpiši
              </button>
            </div>
          </form>
        </div>

        <div className={`${su.card} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={su.tableHead}>
                <tr>
                  <th className={su.th}>Datum</th>
                  <th className={su.th}>Sredstvo</th>
                  <th className={su.thRight}>Kol.</th>
                  <th className={su.th}>Razlog</th>
                  <th className={su.th}>Napomena</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/70">
                {filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className={su.empty}>Nema otpisa sredstava</td>
                  </tr>
                ) : (
                  filteredTransactions.map((tx) => (
                    <tr key={tx._id} className={su.tableRow}>
                      <td className={`${su.td} text-gray-300`}>
                        {tx.documentDate ? new Date(tx.documentDate).toLocaleDateString('sr-RS') : '-'}
                      </td>
                      <td className={su.td}>
                        {typeof tx.itemId === 'object'
                          ? `${tx.itemId.itemCode ? `${tx.itemId.itemCode} - ` : ''}${tx.itemId.name}`
                          : '-'}
                      </td>
                      <td className={`${su.td} text-gray-300 text-right`}>{tx.quantity}</td>
                      <td className={`${su.td} text-gray-300`}>{tx.reason || '-'}</td>
                      <td className={`${su.td} text-gray-400`}>{tx.notes || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetWriteOffView;
